import { useCallback, useEffect, useState } from 'react';

import { useTriageHoF } from 'actions/hall-of-fame';

import { OTHER_EXAM_KEY, TIER } from '../constants';

const examKeyOf = row => ((row && row.submission) || {}).examKey;
const overrideOf = row => (row && row.exam_override) || {};

// Whether this row needs the moderator to write explainer copy before it can become an
// achievement. Every free-text achievement shares one library entry whose explainer is
// empty, so a card built from it would publish a blank paragraph — the service refuses it
// for the same reason, and this is what stops us offering a button it will reject.
const needsExplainer = (row, tier) =>
  tier === TIER.HOF && examKeyOf(row) === OTHER_EXAM_KEY;

// The pending tier verdict on the open record, and the copy that has to travel with it.
// Shaped like `use-record-editor`: isDirty, saving, save.
const useRecordTriage = ({ row, refresh, reportOutcome, closeEditor }) => {
  const [tier, setTier] = useState(null);
  const [explainer, setExplainer] = useState('');
  const [category, setCategory] = useState('');

  const rowId = row && row.id;

  // Reset when the drawer opens on a different record, so one row's decision is never
  // pre-filled onto the next. The stored override seeds the fields, because re-tiering a
  // row that already carries copy should show what it says rather than an empty box.
  useEffect(() => {
    const stored = overrideOf(row);
    setTier(null);
    setExplainer(stored.explainer || '');
    setCategory(stored.category || '');
  }, [rowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { post: postTriage, isProcessing: saving } = useTriageHoF({
    onComplete: reportOutcome('Triage', () => {
      closeEditor();
      refresh();
    }),
  });

  const explainerRequired = needsExplainer(row, tier);
  const explainerMissing = explainerRequired && !explainer.trim();
  const isDirty = !!tier;

  const save = useCallback(() => {
    if (!tier || explainerMissing) return;
    const body = { tier };
    // Only sent where it means something. On any other row the service would store
    // moderator copy against a library entry that already has its own.
    if (explainerRequired) {
      body.explainer = explainer.trim();
      if (category.trim()) body.category = category.trim();
    }
    postTriage(body, rowId);
  }, [
    tier,
    explainerMissing,
    explainerRequired,
    explainer,
    category,
    postTriage,
    rowId,
  ]);

  return {
    tier,
    setTier,
    explainer,
    setExplainer,
    category,
    setCategory,
    explainerRequired,
    explainerMissing,
    isDirty,
    saving,
    save,
  };
};

export default useRecordTriage;
